import { useTranslation } from 'react-i18next'

import { CountryDropdown } from '@/components/ui/CountryDropdown'
import { FormSheet } from '@/components/ui/FormSheet'
import { useFormDraft } from '@/components/ui/useFormDraft'
import {
  Checkbox,
  Field,
  Textarea} from '@/components/ui/Field'
import { findCountry } from '@/lib/countries'
import { Input } from '@/components/ui/Input'
import { ContactsEditor } from '@/features/directories/components/ContactsEditor'
import {
  contactToPayload,
  type ContactPayload,
} from '@/features/directories/api/useDirectory'
import { useContactsEditable } from '@/features/directories/useContactsEditable'
import type { Client, TransportRef } from '@/types/domain'

/* The directory editors.
 *
 * Each is the same shape — a FormSheet, a draft in local state, one required
 * field — and they stay separate components rather than one generic one because
 * the FIELDS differ in kind, not just in label: a client has a registry
 * number, a transport ref has a code. A single schema-driven form would need a
 * config language richer than the forms it replaced. The user editor left for
 * Settings with the account list (S-7): `features/settings/components/UserFormSheet`;
 * the counterparty editor left with the counterparties (S-22b):
 * `features/counterparties/components/CounterpartyFormSheet`.
 *
 * `record` present means edit, absent means add. That one prop is what makes
 * these serve both actions: the surfaces below never mount two sheets.
 *
 * Suppressed values (AD-6) are never loaded into an editable input — a Sales
 * user editing a client must not have "Contact restricted" written back over a
 * real contact. The field renders empty and disabled instead.
 */

/* ── Client ───────────────────────────────────────────────────────────────── */

export interface ClientFormSheetProps {
  open: boolean
  onClose: () => void
  /** Absent for a new record. */
  record?: Client
  onSubmit: (values: ClientDraft) => void
  pending?: boolean
  /** The server's refusal of the last save, already worded — see
   *  `clientSaveErrorMessage`. Shown above the fields, where the reader who
   *  pressed Save is looking; the page behind the sheet is under its scrim. */
  error?: string
}

/* There is deliberately no NUMBER in the draft. The registry number is minted
 * server-side inside the create, from one company-wide series, and is never
 * editable afterwards — it reaches printed documents. A field here would be an
 * input the server ignores. */
export interface ClientDraft {
  name: string
  code: string
  taxId: string
  registrationId: string
  country: string
  address: string
  contacts: ContactPayload[]
}

export function ClientFormSheet({
  open,
  onClose,
  record,
  onSubmit,
  pending,
  error,
}: ClientFormSheetProps) {
  const { t } = useTranslation()
  const contactsEditable = useContactsEditable(record)
  const [draft, setDraft, touched, markTouched] = useFormDraft<ClientDraft>(
    open ? (record?.id ?? 'new') : null,
    () =>
      record
        ? {
            name: record.name,
            code: record.code ?? '',
            taxId: record.taxId ?? '',
            registrationId: record.registrationId ?? '',
            country: record.country,
            address: record.address ?? '',
            contacts: (record.contacts ?? []).map(contactToPayload),
          }
        : emptyClient,
  )

  /* The draft is reset when the sheet OPENS, not when it closes: resetting on
     close would blank the fields during the exit transition, so the sheet would
     be seen emptying itself on its way out. */
  const nameError = touched && !draft.name.trim() ? t('common.form.required') : undefined

  /* A shape check only. The server owns real validation — the code reaches a
     document number, so its rule lives where the number is minted — and a
     second copy here would drift. This catches the obvious before a round
     trip. */
  const codeError =
    touched && draft.code.trim() && !/^[A-Za-z0-9][A-Za-z0-9-]*$/.test(draft.code.trim())
      ? t('directories.clients.codeInvalid')
      : undefined

  const invalid = !draft.name.trim() || Boolean(codeError)

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={record ? t('directories.clients.edit') : t('directories.clients.new')}
      pending={pending}
      error={touched && invalid ? t('common.form.fixFields') : error}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      {/* The number, as a fact rather than a field: an existing client shows
          the one it holds, a new one says where its number will come from. */}
      <div className="bg-surface-sunken border-line flex flex-col gap-1 rounded-md border px-3 py-2.5">
        <span className="asa-eyebrow">{t('directories.clients.number')}</span>
        {record ? (
          <span className="text-text-heading font-mono text-[13px] font-semibold">
            {record.number}
          </span>
        ) : (
          <span className="text-text-muted text-[12.5px] leading-snug font-light">
            {t('directories.clients.numberAssigned')}
          </span>
        )}
      </div>

      <Field label={t('common.fields.name')} required error={nameError}>
        {(props) => (
          <Input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            {...props}
          />
        )}
      </Field>

      {/* The code expands into a document number (FR-39, FR-68), which is why
          it is monospace and why the hint says where it shows up: somebody
          choosing one should know it will be read aloud on the phone. */}
      <Field
        label={t('directories.clients.code')}
        hint={t('directories.clients.codeHint')}
        error={codeError}
      >
        {(props) => (
          <Input
            value={draft.code}
            onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
            className="font-mono"
            {...props}
          />
        )}
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('directories.clients.taxId')}>
          {(props) => (
            <Input
              value={draft.taxId}
              onChange={(e) => setDraft({ ...draft, taxId: e.target.value })}
              {...props}
            />
          )}
        </Field>

        <Field label={t('directories.clients.registrationId')}>
          {(props) => (
            <Input
              value={draft.registrationId}
              onChange={(e) => setDraft({ ...draft, registrationId: e.target.value })}
              {...props}
            />
          )}
        </Field>
      </div>

      {/* The picker, as on the counterparty sheet and for the same reason:
          typed, one country arrives in four spellings no report can group. A client saved while this was free text keeps its value,
          offered as a carried-over row and saved back untouched — nothing
          already stored is rewritten. */}
      <Field label={t('common.fields.country')}>
        {(props) => (
          <CountryDropdown
            value={draft.country}
            onChange={(country) => setDraft({ ...draft, country })}
            {...props}
          />
        )}
      </Field>

      <Field label={t('directories.clients.address')}>
        {(props) => (
          <Textarea
            value={draft.address}
            onChange={(e) => setDraft({ ...draft, address: e.target.value })}
            {...props}
          />
        )}
      </Field>

      {/* Resolved, never the raw string: a legacy free-text country is not a
          code, and undefined lets the phone keep its own local default. */}
      <ContactsEditor
        contacts={draft.contacts}
        editable={contactsEditable}
        defaultCountry={findCountry(draft.country)?.code}
        onChange={(contacts) => setDraft({ ...draft, contacts })}
      />
    </FormSheet>
  )
}

const emptyClient: ClientDraft = {
  name: '',
  code: '',
  taxId: '',
  registrationId: '',
  country: '',
  address: '',
  contacts: [],
}

/* ── Transport reference ──────────────────────────────────────────────────── */

export interface TransportDraft {
  label: string
  code: string
  kind: TransportRef['kind']
  active: boolean
}

export function TransportFormSheet({
  open,
  onClose,
  record,
  kind,
  onSubmit,
  pending,
}: {
  open: boolean
  onClose: () => void
  record?: TransportRef
  /** Which group the add button belongs to — modes or equipment. */
  kind: TransportRef['kind']
  onSubmit: (values: TransportDraft) => void
  pending?: boolean
}) {
  const { t } = useTranslation()
  const [draft, setDraft, touched, markTouched] = useFormDraft<TransportDraft>(
    // The kind is part of the identity: the same sheet serves both lists, and
    // switching which add button was pressed must re-seed it.
    open ? `${record?.id ?? 'new'}:${kind}` : null,
    () =>
      record
        ? {
            label: record.label,
            code: record.code,
            kind: record.kind,
            active: record.isActive,
          }
        : { ...emptyTransport, kind },
  )

  const labelError =
    touched && !draft.label.trim() ? t('common.form.required') : undefined
  const codeError = touched && !draft.code.trim() ? t('common.form.required') : undefined
  const invalid = !draft.label.trim() || !draft.code.trim()

  const heading =
    draft.kind === 'mode'
      ? t('directories.transport.mode')
      : t('directories.transport.equipment')

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={
        record
          ? t('directories.transport.editItem', { item: heading })
          : t('directories.transport.addItem', { item: heading })
      }
      pending={pending}
      error={touched && invalid ? t('common.form.fixFields') : undefined}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      <Field label={t('common.fields.name')} required error={labelError}>
        {(props) => (
          <Input
            value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <Field
        label={t('directories.transport.code')}
        required
        error={codeError}
        hint={t('directories.transport.codeHint')}
      >
        {(props) => (
          <Input
            value={draft.code}
            // Codes are the machine-facing half of the pair and are always
            // upper-case, so the field enforces it rather than asking.
            onChange={(e) =>
              setDraft({ ...draft, code: e.target.value.toUpperCase() })
            }
            className="font-mono"
            {...props}
          />
        )}
      </Field>

      <Checkbox
        label={t('common.form.active')}
        checked={draft.active}
        onCheckedChange={(checked) => setDraft({ ...draft, active: checked })}
      />
    </FormSheet>
  )
}

const emptyTransport: TransportDraft = {
  label: '',
  code: '',
  kind: 'mode',
  active: true,
}

/* Re-exported so the settings surface can build its own editors on the same
   Textarea control without importing from two places. */
export { Textarea }
